import { useState, useEffect } from "react";
import { workspaceApi, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Loader2, AlertTriangle, RefreshCw, ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { PageHeader, SectionCard } from "../components/ui/page-shell";
import { toast } from "sonner";

export default function ErrorLog() {
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [clearingOld, setClearingOld] = useState(false);

  useEffect(() => {
    loadErrors();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => { loadErrors(); }, 15000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  const loadErrors = async () => {
    setLoading(true);
    try {
      const res = await workspaceApi.getErrors(100);
      setErrors(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };


  const handleDeleteOne = async (errorId) => {
    if (!window.confirm("Delete this error log entry?")) return;
    try {
      await workspaceApi.deleteError(errorId);
      setErrors(prev => prev.filter(e => e.id !== errorId));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const handleClearErrors = async () => {
    if (!window.confirm("Clear ALL error logs for this workspace? This cannot be undone.")) return;
    try {
      await workspaceApi.clearErrors();
      toast.success("Error log cleared");
      setErrors([]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleClearOld = async () => {
    setClearingOld(true);
    try {
      const res = await workspaceApi.clearOldErrors(30);
      toast.success(`Removed ${res?.data?.deleted ?? 0} entries older than 30 days`);
      await loadErrors();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to clear old errors");
    } finally {
      setClearingOld(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="error-log-page">
      <div className="flex items-center justify-between">
        <div>
          <PageHeader title="Error Log" subtitle="Recent backend errors logged for your workspace." />
        </div>
        <div className="flex gap-2"><Button variant="outline" onClick={handleClearErrors} className="rounded-none text-red-600 hover:text-red-700" data-testid="clear-errors-btn"><Trash2 className="w-4 h-4 mr-2" /> Clear All</Button><Button variant="outline" onClick={loadErrors} className="rounded-none" data-testid="refresh-errors-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button><Button variant="outline" onClick={handleClearOld} disabled={clearingOld} className="rounded-none" data-testid="clear-old-errors-btn" title="Remove errors older than 30 days">
            {clearingOld ? "Clearing..." : "Clear >30d"}
          </Button>
          <label className="flex items-center gap-1 text-xs cursor-pointer select-none px-2" data-testid="autorefresh-toggle">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            Auto-refresh
          </label></div>
      </div>

      {errors.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border">
          <AlertTriangle className="w-12 h-12 mx-auto text-[hsl(var(--success-green))] mb-4" />
          <h3 className="font-semibold mb-2">No errors logged</h3>
          <p className="text-muted-foreground">Your chatbots are running smoothly!</p>
        </div>
      ) : (
        <div className="border border-border divide-y divide-border">
          <div className="grid grid-cols-12 p-4 table-header">
            <div className="col-span-1"></div>
            <div className="col-span-3">Timestamp</div>
            <div className="col-span-2">Chatbot</div>
            <div className="col-span-2">Context</div>
            <div className="col-span-4">Error</div>
          </div>
          <ScrollArea className="h-[600px]">
            {errors.map(error => (
              <div key={error.id} className="border-b border-border last:border-0">
                <div
                  className="grid grid-cols-12 p-4 hover:bg-muted/30 cursor-pointer transition-colors"
                  onClick={() => toggleExpand(error.id)}
                  data-testid={`error-row-${error.id}`}
                >
                  <div className="col-span-1">
                    {expanded[error.id] ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="col-span-3 text-sm">
                    {new Date(error.timestamp).toLocaleString()}
                  </div>
                  <div className="col-span-2 text-sm truncate">
                    {error.chatbot_id?.substring(0, 8)}...
                  </div>
                  <div className="col-span-2">
                    <span className="status-badge status-failed">{error.context || "unknown"}</span>
                  </div>
                  <div className="col-span-4 text-sm truncate text-[hsl(var(--signal-red))] flex items-center justify-between gap-2">
                    <span className="truncate">{error.error}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleDeleteOne(error.id); }}
                      className="shrink-0 p-1 rounded hover:bg-red-100 text-red-600 hover:text-red-700"
                      title="Delete this entry"
                      data-testid="errorlog-delete-row"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"></path></svg>
                    </button>
                  </div>
                </div>
                {expanded[error.id] && (
                  <div className="px-4 pb-4 bg-muted/20">
                    <div className="p-4 bg-background border border-border">
                      <p className="overline mb-2">Full Error</p>
                      <pre className="text-sm font-mono whitespace-pre-wrap text-[hsl(var(--signal-red))]">
                        {error.error}
                      </pre>
                      {error.request_context && (
                        <>
                          <p className="overline mt-4 mb-2">Request Context</p>
                          <pre className="text-sm font-mono whitespace-pre-wrap text-muted-foreground">
                            {JSON.stringify(error.request_context, null, 2)}
                          </pre>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
